import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { TodayResults } from './TodayResults';

const CATS = [{ id: 'GR', label: 'GOLDEN RAJA' }];
const GR_TABS = ['IMAGES', '5D', '4D', '3D'];
const DEAR_TABS = ['5D', '4D', '3D'];
const GR_COLS = [
  ['11am', '11 AM'],
  ['12pm', '12 PM'],
  ['5pm', '5 PM'],
  ['7pm', '7 PM'],
];
const DEAR_COLS = [
  ['dear1pm', '1 PM'],
  ['dear6pm', '6 PM'],
  ['dear8pm', '8 PM'],
];
const KL_COLS = [
  [6, '6 NO'],
  [5, '5 NO'],
  [4, '4 NO'],
  [3, '3 NO'],
];
const ALL_COLS = [
  ['11am', '11 AM'],
  ['12pm', '12 PM'],
  ['dear1pm', '1 PM'],
  ['5pm', '5 PM'],
  ['dear6pm', '6 PM'],
  ['7pm', '7 PM'],
  ['dear8pm', '8 PM'],
];
const fmtD = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};
const digitsTail = (v, n) => {
  const digs = (v || '').replace(/\D/g, '');
  if (!digs) return null;
  return digs.length >= n ? digs.slice(-n) : digs;
};

const CHART_KEY = {
  GR: { '5D': 'gr5d', '4D': 'gr4d', '3D': 'gr3d' },
  DEAR: { '5D': 'dear5d', '4D': 'dear4d', '3D': 'dear3d' },
  ALL: { '5D': 'comb5d', '4D': 'comb4d', '3D': 'comb3d' },
};
const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export function ResultsChart({ onClose }) {
  const [cat, setCat] = useState('GR');
  const [tab, setTab] = useState('IMAGES');
  const [grDays, setGrDays] = useState(null);
  const [extDays, setExtDays] = useState(null);
  const [month, setMonth] = useState('');
  const [date, setDate] = useState('');
  const [imgBusy, setImgBusy] = useState(false);

  const chartKey = cat === 'KL' ? 'kl' : CHART_KEY[cat]?.[tab];
  const chartDate = () => {
    const today = istToday();
    if (!month || today.startsWith(month)) return today;
    const [y, m] = month.split('-').map(Number);
    return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
  };

  const fetchChartBlob = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/charts/image?key=${chartKey}&date=${chartDate()}`
    );
    if (!res.ok) throw new Error('fetch failed');
    return res.blob();
  };

  const downloadChart = async () => {
    setImgBusy(true);
    try {
      const blob = await fetchChartBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `golden-raja-${chartKey}-chart-${chartDate()}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Chart downloaded');
    } catch {
      toast.error('Download failed');
    } finally {
      setImgBusy(false);
    }
  };

  const shareChart = async () => {
    setImgBusy(true);
    try {
      const blob = await fetchChartBlob();
      const file = new File([blob], `golden-raja-${chartKey}-chart.jpg`, { type: 'image/jpeg' });
      const text = `📊 GOLDEN RAJA MONTHLY CHART\n🎯 Watch LIVE 👉 https://goldenraja.in/live`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text });
          return;
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } catch {
      toast.error('Share failed');
    } finally {
      setImgBusy(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/charts`)
      .then((r) => setGrDays(r.data))
      .catch(() => setGrDays([]));
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/charts/external`)
      .then((r) => setExtDays(r.data))
      .catch(() => setExtDays([]));
  }, []);

  const switchCat = (c) => {
    setCat(c);
    setTab(c === 'GR' ? 'IMAGES' : '5D');
    setMonth('');
    setDate('');
  };

  const mergedDays = (() => {
    if (!grDays || !extDays) return null;
    const map = {};
    grDays.forEach((d) => {
      map[d.date] = { ...(map[d.date] || {}), ...d.shows };
    });
    extDays.forEach((d) => {
      map[d.date] = { ...(map[d.date] || {}), ...d.shows };
    });
    return Object.keys(map)
      .sort()
      .reverse()
      .map((k) => ({ date: k, shows: map[k] }));
  })();

  const days = cat === 'GR' ? grDays : cat === 'ALL' ? mergedDays : extDays;
  const showsFilter = (d) =>
    cat === 'GR' || cat === 'ALL'
      ? true
      : cat === 'DEAR'
        ? DEAR_COLS.some(([id]) => d.shows[id])
        : Boolean(d.shows.kl);
  const catDays = days ? days.filter(showsFilter) : null;

  const tabs = cat === 'GR' ? GR_TABS : cat === 'KL' ? [] : DEAR_TABS;
  const cut = tab === '5D' ? 0 : tab === '4D' ? 1 : 2;
  const cols =
    cat === 'GR' ? GR_COLS : cat === 'DEAR' ? DEAR_COLS : cat === 'ALL' ? ALL_COLS : KL_COLS;

  const months = catDays
    ? [...new Set(catDays.map((d) => d.date.slice(0, 7)))].sort().reverse()
    : [];
  const monthLabel = (m) => {
    const [y] = m.split('-');
    return `${new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })} ${y}`;
  };
  const filtered = catDays
    ? catDays.filter(
        (d) => (!month || d.date.startsWith(month)) && (!date || d.date === date)
      )
    : null;

  // ledger view: oldest at top → today → one empty row for tomorrow at the bottom
  const ledgerRows = (() => {
    if (!filtered) return null;
    if (date) return filtered;
    const map = Object.fromEntries(filtered.map((d) => [d.date, d.shows]));
    const today = istToday();
    const keys = Object.keys(map).sort();
    let start;
    let end;
    if (month) {
      start = `${month}-01`;
      end = today.startsWith(month) ? addDays(today, 1) : keys[keys.length - 1];
      if (!end) return [];
    } else {
      if (!keys.length) return [];
      start = keys[0];
      end = addDays(today, 1);
    }
    const rows = [];
    for (let d = start; d <= end && rows.length < 400; d = addDays(d, 1)) {
      rows.push({ date: d, shows: map[d] || {} });
    }
    return rows;
  })();

  const cellValue = (d, col) => {
    if (cat === 'KL') return digitsTail(d.shows.kl, col) || '—';
    const need = 5 - cut;
    return digitsTail(d.shows[col], need) || '—';
  };

  const showTable = cat !== 'GR' || tab !== 'IMAGES';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#050505]" data-testid="results-chart-overlay">
      <div className="flex items-center justify-between border-b border-[#F5C542]/25 px-4 py-3 md:px-8">
        <h2 className="font-display text-lg uppercase tracking-[0.2em] text-[#F5C542] md:text-xl">
          Golden Raja Results
        </h2>
        <button
          type="button"
          onClick={onClose}
          data-testid="results-chart-close"
          className="border border-white/20 p-2 text-white hover:border-[#F5C542] hover:text-[#F5C542]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-3 md:px-8">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`chart-tab-${t}`}
            className={`px-4 py-2 font-display text-xs tracking-[0.2em] transition-colors md:px-6 md:text-sm ${
              tab === t
                ? 'bg-[#F5C542] text-black'
                : 'border border-white/20 text-white hover:border-[#F5C542]/60'
            }`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {showTable && (
            <>
              <button
                type="button"
                onClick={downloadChart}
                disabled={imgBusy}
                data-testid="chart-download-btn"
                className="flex items-center gap-1.5 border border-[#F5C542]/50 px-3 py-2 text-xs uppercase tracking-[0.1em] text-[#F5C542] hover:bg-[#F5C542] hover:text-black disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Download</span>
              </button>
              <button
                type="button"
                onClick={shareChart}
                disabled={imgBusy}
                data-testid="chart-share-btn"
                className="flex items-center gap-1.5 border border-green-500/50 px-3 py-2 text-xs uppercase tracking-[0.1em] text-green-400 hover:bg-green-500 hover:text-black disabled:opacity-50"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Share</span>
              </button>
              <select
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setDate('');
                }}
                data-testid="chart-month-filter"
                className="border border-white/20 bg-black px-3 py-2 text-xs uppercase tracking-[0.1em] text-white outline-none focus:border-[#F5C542]"
              >
                <option value="">All Months</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="chart-date-search"
                className="border border-white/20 bg-black px-3 py-1.5 text-xs text-white outline-none focus:border-[#F5C542]"
              />
              {(month || date) && (
                <button
                  type="button"
                  onClick={() => {
                    setMonth('');
                    setDate('');
                  }}
                  data-testid="chart-filter-clear"
                  className="border border-[#F5C542]/50 px-3 py-2 text-xs uppercase tracking-[0.1em] text-[#F5C542] hover:bg-[#F5C542] hover:text-black"
                >
                  Clear
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-8 md:px-8">
        {!showTable ? (
          <TodayResults />
        ) : (
          <table className="w-full border-collapse text-center" data-testid="results-chart-table">
            <thead className="sticky top-0 bg-[#0A160D]">
              <tr>
                <th className="border border-[#F5C542]/25 px-2 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[#F5C542] md:text-xs">
                  Date
                </th>
                {cols.map(([, label]) => (
                  <th
                    key={label}
                    className="border border-[#F5C542]/25 px-2 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[#F5C542] md:text-xs"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ledgerRows === null && (
                <tr>
                  <td colSpan={cols.length + 1} className="py-10 text-sm text-[#666]">Loading…</td>
                </tr>
              )}
              {ledgerRows && ledgerRows.length === 0 && (
                <tr>
                  <td colSpan={cols.length + 1} className="py-10 text-sm text-[#666]">No results found</td>
                </tr>
              )}
              {ledgerRows &&
                ledgerRows.map((d) => (
                  <tr key={d.date} data-testid={`chart-row-${d.date}`}>
                    <td className="border border-white/10 px-2 py-2.5 text-xs text-[#A3A3A3] md:text-sm">
                      {fmtD(d.date)}
                    </td>
                    {cols.map(([id]) => (
                      <td
                        key={id}
                        className="font-display border border-white/10 px-1 py-2.5 text-sm tracking-[0.1em] text-white md:px-2 md:text-lg md:tracking-[0.15em]"
                      >
                        {cellValue(d, id)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
